<template>
    <Intersect
        :threshold="[0, 0]"
        @leave="unFocus"
        @enter="focus">
        <section
            ref="section"
            class="bg-black-700 full-width tablet:min-h-screen flex flex-col tabletLandscape:flex-row relative focus:outline-none"
            tabindex="0"
            @keyup.left="previousSlide"
            @keyup.right="nextSlide"
        >
            <div class="product-gallery h-screen relative flex flex-col align-center overflow-hidden">
                <slot
                    name="product-selection"
                    :products="products"
                    :currentProductSlug="currentProductSlug"
                    :onProductChange="productChange"
                />

                <div class="flex h-screen items-center justify-between">
                    <product-gallery-paginator
                        v-if="hasPreviousSlide"
                        direction="previous"
                        :handle-pagination="previousSlide"
                        class="mr-auto ml-6 py-4 px-2 border-2 border-gold-500"
                    />

                    <product-gallery-image
                        :key="currentSlideIndex"
                        :src="getCurrentSlideProperty('image_url')"
                        :srcset="getCurrentSlideProperty('image_src_set')"
                        :alt="getCurrentSlideProperty('alt')"
                    />

                    <product-gallery-paginator
                        v-if="hasNextSlide"
                        direction="next"
                        :handle-pagination="nextSlide"
                        class="ml-auto mr-6 py-4 px-2 border-2 border-gold-500"
                    />
                </div>

                <div class="unmasked-thumbnail-wrapper absolute bottom-0 flex flex-col self-center items-center">
                    <product-gallery-caption
                        v-if="withCaption"
                        :pre-title="getCurrentSlideProperty('pre_title')"
                        :title="getCurrentSlideProperty('title')"
                    />

                    <product-gallery-thumbnail-controls
                        v-if="withThumbnailControls"
                        :slides="slides"
                        :current-slide-index="currentSlideIndex"
                        :swipe-instructions="swipeInstructions"
                        @thumbnailClicked="setCurrentSlide"
                    />
                </div>
            </div>

            <slot
                name="product-description"
                :products="products"
                :currentProductSlug="currentProductSlug"
            />
        </section>
    </Intersect>
</template>

<script>
import Intersect from 'vue-intersect';
import {
  find, get, orderBy, keyBy,
} from 'lodash';
import ProductGalleryImage from '@web/components/ProductGalleries/ProductGalleryImage';
import ProductGalleryCaption from '@web/components/ProductGalleries/ProductGalleryCaption';
import ProductGalleryThumbnailControls from '@web/components/ProductGalleries/controls/ThumbnailControls';
import ProductGalleryPaginator from '@web/components/ProductGalleries/controls/Paginator';

export default {
  components: {
    ProductGalleryPaginator,
    Intersect,
    ProductGalleryImage,
    ProductGalleryCaption,
    ProductGalleryThumbnailControls,
  },
  props: {
    products: {
      type: Object,
      required: true,
    },
    unorderedSlides: {
      // this is sanitized to "slides" in the computed properties below
      type: Array,
      required: true,
    },
    swipeInstructions: {
      type: String,
      required: true,
    },
    withPagination: {
      type: Boolean,
      required: false,
      default: true,
    },
    withThumbnailControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    withCaption: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultProduct: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      currentProductSlug: get(this, 'defaultProduct') || Object.keys(this.products)[0],
      currentSlideIndex: 0,
    };
  },

  computed: {
    slides() {
      return keyBy(orderBy(this.unorderedSlides, 'index'), 'index');
    },
    totalSlides() {
      return Object.keys(this.slides).length;
    },
    hasPreviousSlide() {
      return this.currentSlideIndex > 0;
    },
    hasNextSlide() {
      return this.currentSlideIndex < this.totalSlides;
    },
  },

  mounted() {
    const currentProductIndex = get(find(this.slides, { product_slug: get(this, 'defaultProduct') }), 'index', 0);
    if (currentProductIndex !== this.currentSlideIndex) {
      this.setCurrentSlide(currentProductIndex);
    }
  },

  methods: {
    // the focus/unfocus makes the keyboard events work
    // without this, vue's keyup doesn't fire
    // <section>'s can't have a focus by default in html
    // so that's why we have the tabIndex=0
    // and to prevent the browser default outline,
    // we have focus:outline-none
    // also, chrome by default will scroll to the focused section
    // so we reset the scroll back to where it was by default
    focus() {
      const x = window.scrollX; const
        y = window.scrollY;
      this.$refs.section.focus();
      window.scrollTo(x, y);
    },

    unFocus() {
      this.$refs.section.blur();
    },
    setCurrentProductFromCurrentSlide() {
      this.currentProductSlug = get(this.slides, [this.currentSlideIndex, 'product_slug']);
    },
    previousSlide() {
      // prevent going lower than 0
      this.setCurrentSlide(Math.max(
        this.currentSlideIndex - 1,
        0,
      ));
    },
    nextSlide() {
      // prevent going higher than the last slide
      this.setCurrentSlide(Math.min(
        this.currentSlideIndex + 1,
        this.totalSlides,
      ));
    },
    setCurrentSlide(index) {
      this.currentSlideIndex = parseInt(index, 10);
      this.setCurrentProductFromCurrentSlide();
    },
    productChange(newProductSlug) {
      this.currentProductSlug = newProductSlug;

      // go to the first slide attributed to the passed product
      this.currentSlideIndex = get(find(this.slides, { product_slug: newProductSlug }), 'index');
    },
    getCurrentSlideProperty(property) {
      return get(this.slides, [this.currentSlideIndex, property]);
    },
  },
};
</script>

<style scoped>
</style>
